import type { Game } from '@/types/games';
import type { MlRecommendationItemData, MlRecommendationTracking } from '@/types/tracking/ml-recommendation';

type RecommendationGame = Game & {
    rank_order?: number | string | null;
    similarity_score?: number | string | null;
};

export type RecommendationSlicePayload = {
    items?: RecommendationGame[];
    rank_order?: Array<number | string | null>;
    similarity_score?: Array<number | string | null>;
    internal_code?: string | null;
    anchor_game_uid?: string | null;
    embedding_type?: string | null;
    embedding_version?: string | null;
    dl_timestamp_utc?: string | null;
    location?: string | null;
};

type RecommendationItemOptions = {
    includeModelScore: boolean;
    deriveRankOrder: boolean;
    sourceIndexes?: number[];
};

const toNullableString = (value: string | null | undefined): string | null => {
    const normalized = value?.trim();
    return normalized || null;
};

const toNumber = (value: number | string | null | undefined): number | null => {
    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    if (normalizedValue === null || normalizedValue === undefined || normalizedValue === '') {
        return null;
    }

    const normalized = Number(normalizedValue);
    return Number.isFinite(normalized) ? normalized : null;
};

const toInteger = (value: number | string | null | undefined): number | null => {
    const normalized = toNumber(value);
    return normalized !== null && Number.isInteger(normalized) ? normalized : null;
};

const getRecommendationItems = (
    slice: RecommendationSlicePayload,
    { includeModelScore, deriveRankOrder, sourceIndexes }: RecommendationItemOptions
): MlRecommendationItemData[] =>
    (sourceIndexes ?? (slice.items ?? []).map((_, index) => index)).flatMap((index) => {
        const game = slice.items?.[index];
        if (!game) {
            return [];
        }

        const itemId = toNullableString(game.uid);
        const rankOrderSource = slice.rank_order === undefined ? game.rank_order : slice.rank_order[index];
        const rankOrder = deriveRankOrder ? index + 1 : toInteger(rankOrderSource);

        if (!itemId || rankOrder === null) {
            return [];
        }

        const modelScoreSource =
            slice.similarity_score === undefined ? game.similarity_score : slice.similarity_score[index];

        return [
            {
                itemId,
                rankOrder,
                modelScore: includeModelScore ? toNumber(modelScoreSource) : null
            }
        ];
    });

export const getRecommendationSourceIndexes = (sourceItems: Game[], renderedItems: Game[]): number[] => {
    const sourceIndexById = new Map(sourceItems.map((item, index) => [item.id, index]));
    return renderedItems.flatMap((item) => {
        const sourceIndex = sourceIndexById.get(item.id);
        return sourceIndex === undefined ? [] : [sourceIndex];
    });
};

export const getTopPicksForYouRecommendation = (
    slice: RecommendationSlicePayload,
    renderedItemSourceIndexes?: number[]
): MlRecommendationTracking => ({
    recommendation: {
        modelName: 'top_picks_for_you',
        modelVersion: null,
        modelStatus: null,
        inferenceTimestampUtc: null,
        location: null,
        triggerItemId: null,
        modelType: null
    },
    items: getRecommendationItems(slice, {
        includeModelScore: false,
        deriveRankOrder: true,
        sourceIndexes: renderedItemSourceIndexes
    })
});

export const getBecauseYouPlayedRecommendation = (
    slice: RecommendationSlicePayload,
    renderedItemSourceIndexes?: number[]
): MlRecommendationTracking => ({
    recommendation: {
        modelName: 'byp',
        modelVersion: null,
        modelStatus: null,
        inferenceTimestampUtc: null,
        location: null,
        triggerItemId: toNullableString(slice.internal_code),
        modelType: null
    },
    items: getRecommendationItems(slice, {
        includeModelScore: false,
        deriveRankOrder: false,
        sourceIndexes: renderedItemSourceIndexes
    })
});

export const getSimilarGamesRecommendation = (
    slice: RecommendationSlicePayload,
    renderedItemSourceIndexes?: number[]
): MlRecommendationTracking => ({
    recommendation: {
        modelName: 'similar_games',
        modelVersion: toNullableString(slice.embedding_version),
        modelStatus: null,
        inferenceTimestampUtc: toNullableString(slice.dl_timestamp_utc),
        location: toNullableString(slice.location),
        triggerItemId: toNullableString(slice.anchor_game_uid),
        modelType: toNullableString(slice.embedding_type)
    },
    items: getRecommendationItems(slice, {
        includeModelScore: true,
        deriveRankOrder: false,
        sourceIndexes: renderedItemSourceIndexes
    })
});
